import { Loader2 } from 'lucide-react';
import { Alert } from '@/components/ui/alert';
import { getErrorMessage } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * Confirmation gate for irreversible actions.
 *
 * The confirm button carries the *specific* verb ("Delete server") rather than a bare
 * "OK", so someone skimming still reads what is about to happen.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  destructive = true,
  busy = false,
  error = null,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel?: string;
  destructive?: boolean;
  busy?: boolean;
  /**
   * Why the last attempt failed.
   *
   * ## The dialog had nowhere to put this, and it made every refusal look like a broken button
   *
   * A failed confirmation leaves the dialog open, because the caller only closes it on success.
   * The error itself went to an alert at the top of the page -- underneath the modal that is still
   * covering it. So the customer clicked Delete, watched nothing change, and reasonably concluded
   * the button did not work.
   *
   * Reported on the servers page, where deleting a credential still in use is refused. The refusal
   * was correct, well worded, and invisible. Ten pages share this dialog, so the same
   * silence was waiting behind every one of them.
   */
  error?: unknown;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {/* Inside the dialog, above the buttons: where somebody who has just pressed Confirm is
            already looking. */}
        {error != null && (
          <Alert variant="danger" className="mt-2">
            {getErrorMessage(error)}
          </Alert>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant={destructive ? 'danger' : 'default'} onClick={onConfirm} disabled={busy}>
            {busy && <Loader2 className="animate-spin" />} {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
